import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useHardwareRequestForm } from '../hooks/useHardwareRequestForm';
import { createHardwareRequest } from '../api/client';
import { UserSelectionStep } from '../components/hardwareRequest/steps/UserSelectionStep';
import { HardwareSelectionStep } from '../components/hardwareRequest/steps/HardwareSelectionStep';
import { ReviewStep } from '../components/hardwareRequest/steps/ReviewStep';
import { ROUTES } from '../config/constants';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: tokens.spacingVerticalXXL,
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  stepper: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXXL,
    overflowX: 'auto',
  },
  step: {
    flex: 1,
    minWidth: '140px',
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    borderBottom: `3px solid ${tokens.colorNeutralStroke1}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  stepActive: {
    borderBottomColor: tokens.colorBrandForeground1,
  },
  stepCompleted: {
    borderBottomColor: tokens.colorPaletteGreenForeground1,
  },
  stepNumber: {
    display: 'inline-block',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground4,
    color: tokens.colorNeutralForeground2,
    lineHeight: '32px',
    marginBottom: tokens.spacingVerticalS,
  },
  stepNumberActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  stepNumberCompleted: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  content: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalM,
  },
});

const STEPS = [
  { label: 'Select User', key: 0 },
  { label: 'Select Hardware', key: 1 },
  { label: 'Review & Submit', key: 2 },
];

export function HardwareRequestPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const {
    formData,
    setUserData,
    toggleMainHardware,
    togglePeripheral,
    setMonitorQuantity,
    setJustification,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
  } = useHardwareRequestForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [requestId, setRequestId] = useState<string>('');

  const handleNext = () => {
    if (validateStep(formData.currentStep)) {
      nextStep();
      setSubmitError(null);
    } else {
      if (formData.currentStep === 0) {
        setSubmitError('Please select an employee before continuing.');
      } else if (formData.currentStep === 1) {
        setSubmitError('Please select at least one hardware item and provide justification (min 10 characters).');
      }
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(formData.currentStep)) {
      setSubmitError('Please complete the form before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await createHardwareRequest({
        userId: formData.selectedUserId,
        userDisplayName: formData.selectedUserName,
        userPrincipalName: formData.selectedUserPrincipalName,
        mainHardware: formData.mainHardware,
        peripherals: formData.peripherals,
        monitorQuantity: formData.monitorQuantity,
        justification: formData.justification,
      });

      setRequestId(result.requestId);
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to submit hardware request. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    resetForm();
    navigate(ROUTES.DASHBOARD);
  };

  const isStepComplete = (stepKey: number): boolean => {
    return stepKey < formData.currentStep;
  };

  const canNavigateToStep = (stepKey: number): boolean => {
    if (stepKey > formData.currentStep) return false;
    for (let i = 0; i < stepKey; i++) {
      if (!validateStep(i)) return false;
    }
    return true;
  };

  const renderStepContent = () => {
    switch (formData.currentStep) {
      case 0:
        return <UserSelectionStep formData={formData} onUserSelect={setUserData} />;
      case 1:
        return (
          <HardwareSelectionStep
            formData={formData}
            toggleMainHardware={toggleMainHardware}
            togglePeripheral={togglePeripheral}
            setMonitorQuantity={setMonitorQuantity}
            setJustification={setJustification}
          />
        );
      case 2:
        return <ReviewStep formData={formData} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text size={600} weight="bold">
          Request Hardware
        </Text>
        <Text>Request hardware and peripherals for an employee</Text>
      </div>

      <div className={styles.stepper}>
        {STEPS.map((step) => {
          const isActive = step.key === formData.currentStep;
          const isComplete = isStepComplete(step.key);
          const canNavigate = canNavigateToStep(step.key);

          return (
            <div
              key={step.key}
              className={`${styles.step} ${isActive ? styles.stepActive : ''} ${
                isComplete ? styles.stepCompleted : ''
              }`}
              onClick={() => canNavigate && goToStep(step.key)}
              style={{ cursor: canNavigate ? 'pointer' : 'not-allowed', opacity: canNavigate ? 1 : 0.6 }}
            >
              <div
                className={`${styles.stepNumber} ${isActive ? styles.stepNumberActive : ''} ${
                  isComplete ? styles.stepNumberCompleted : ''
                }`}
              >
                {isComplete ? <Checkmark24Regular /> : step.key + 1}
              </div>
              <Text size={300}>{step.label}</Text>
            </div>
          );
        })}
      </div>

      <div className={styles.content}>{renderStepContent()}</div>

      <div className={styles.actions}>
        <Button
          appearance="secondary"
          icon={<ArrowLeft24Regular />}
          disabled={formData.currentStep === 0 || isSubmitting}
          onClick={prevStep}
        >
          Back
        </Button>

        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
          {formData.currentStep < STEPS.length - 1 ? (
            <Button
              appearance="primary"
              icon={<ArrowRight24Regular />}
              iconPosition="after"
              disabled={!validateStep(formData.currentStep) || isSubmitting}
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <Button
              appearance="primary"
              icon={isSubmitting ? undefined : <Checkmark24Regular />}
              disabled={isSubmitting || !validateStep(formData.currentStep)}
              onClick={handleSubmit}
            >
              {isSubmitting ? <Spinner size="tiny" label="Submitting..." /> : 'Submit Request'}
            </Button>
          )}
        </div>
      </div>

      {submitError && <Text className={styles.error}>{submitError}</Text>}

      <Dialog open={successDialogOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Hardware Request Submitted</DialogTitle>
            <DialogContent>
              <Text>
                Your hardware request has been submitted successfully.
              </Text>
              <Text style={{ marginTop: tokens.spacingVerticalM }}>
                <strong>Request ID:</strong> {requestId}
              </Text>
              <Text style={{ marginTop: tokens.spacingVerticalM }}>
                Line manager and IT administrators have been notified.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={handleSuccessClose}>
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
